import { useState, useCallback } from "react";
import { GLMappingEntity } from "../../domain/entities/GLMappingEntity";
import { DocumentGlType } from "../../domain/entities/AccountTreeEntity";
import { glMappingRepository } from "../../data/repositories/GLMappingRepositoryImpl";
import { flattenSelectableLeaves, treeNodeToGLMappingEntity } from "../../utils/glTreeUtils";

export interface UseGLMappingsReturn {
  glMappings: GLMappingEntity[];
  loading: boolean;
  error: string | null;
  fetchGLMappings: (docType: DocumentGlType) => Promise<void>;
  clearError: () => void;
}

export const useGLMappings = (): UseGLMappingsReturn => {
  const [glMappings, setGLMappings] = useState<GLMappingEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGLMappings = useCallback(async (docType: DocumentGlType): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await glMappingRepository.fetchAllSelectableLeaves(docType);
      if (result.success) {
        setGLMappings(
          flattenSelectableLeaves(result.groups).map(treeNodeToGLMappingEntity),
        );
      } else {
        setError("Failed to fetch GL mappings");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "An error occurred while fetching GL mappings");
      console.error("Error fetching GL mappings:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    glMappings,
    loading,
    error,
    fetchGLMappings,
    clearError,
  };
};
